import { Injectable } from '@angular/core';
import { GlobalsService } from '@services/global.service';
import AppConfiguration from '@hal.common.ui/utilities/AppConfig';
import { DefaultSettingsService as SDKDefaultSettingsService } from '@modules/SDKs/sysnetApi/api/defaultSettings.service';
import { DefaultSettingView } from '@modules/SDKs/sysnetApi';
import Constants from '@hal.common.ui/utilities/Constants';
import { IOrgSettingPromise, ISettingCache, IVenueSettingPromise } from 'src/app/types/interfaces/SettingsService';
import { ServicesWrappingSysnetSDK } from './ServicesWrappingSysnetSDK';

@Injectable({
    providedIn: 'root'
})
export class DefaultSettingsService extends ServicesWrappingSysnetSDK {
    private orgId: string;
    private orgSettingPromises: IOrgSettingPromise[] = [];
    private venueSettingPromises: IVenueSettingPromise[] = [];
    private cacheExpirationTime = 5 * 60 * 1000; // 5 minutes in milliseconds
    private isAllOrgSettingsLoading = false;

    constructor(private sdkDefaultSettingsService: SDKDefaultSettingsService, private globals: GlobalsService) {
        super([sdkDefaultSettingsService], globals);
        this.globals.$state.subscribe(async (newState) => {
            if (newState) {
                this.orgId = newState.selectedOrganisation?._id;
                const hasAccess = newState.user?.access?.length ?
                    newState.user.access.some((access) => access?.role?.application?.code === Constants.APPLICATION_CODES.SYSNET_CLOUD &&
                    access.location.organisationId === this.orgId) : false;
                if (this.orgId && hasAccess && !this.isAllOrgSettingsLoading) {
                    await this.getOrganisationSettings(this.orgId);
                }
            }
        });

        if (this.globals.getState()?.selectedOrganisation?._id) {
            this.orgId = this.globals.getState().selectedOrganisation._id;
            if (!this.isAllOrgSettingsLoading) {
                this.getOrganisationSettings(this.orgId);
            }
        }
    }

    public async getOrganisationSettings(organisationId: string): Promise<DefaultSettingView[]> {
        this.isAllOrgSettingsLoading = true;
        const limit = 200;
        let offset = 0;
        let defaultSettings = [];
        let defaultSettingsRes;
        do {
            defaultSettingsRes = await this.sdkDefaultSettingsService.defaultSettingsGet
                (AppConfiguration.rsh.getStrVal('rid'),
                    'OrganisationSettings',
                    null, null, null, null, null, limit, offset, null, null, null, null, null, null, null,
                    null, null, null, organisationId, null, 'response').toPromise();
            if (defaultSettingsRes?.body?.length > 0) {
                defaultSettings = defaultSettings.concat(defaultSettingsRes.body);
                offset += limit;
            }
        } while (defaultSettings.length < +defaultSettingsRes.headers.get('total_available_results_count'));
        this.isAllOrgSettingsLoading = false;
        this.cacheAllSettings(defaultSettings);
        return defaultSettings;
    }

    public getVenueSettings(venueId: string): Promise<DefaultSettingView[]> {
        return this.sdkDefaultSettingsService.defaultSettingsGet
            (AppConfiguration.rsh.getStrVal('rid'),
            'VenueSettings',
            null, null, null, null, null, 200, 0, null, null, null, null, null, null, null,
            null, null, null, null, venueId, 'body').toPromise();
    }

    public async getOrganisationSettingValue(name: string, organisationId: string = this.orgId): Promise<string> {
        const existingPromise = this.findExistingPromise(name, organisationId, null, 'organisationId');
        if (existingPromise) {
            const promise = await existingPromise.p;
            return promise;
        }
        const settingsCache = localStorage.getItem('defaultSettings');
        const settingsCacheParsed = settingsCache ? JSON.parse(settingsCache) : [];
        const settingFromCache = this.findSettingFromCache(settingsCacheParsed, name, organisationId, null, 'organisationId');
        if (settingFromCache) {
            return settingFromCache.value;
        }
        const settingValue = this.getOrganisationSettingValueByName(name, organisationId);
        this.orgSettingPromises.push({ settingName: name, organisationId, p: settingValue });
        let cachedSetting;
        settingValue.then((value) => {
            this.orgSettingPromises = this.orgSettingPromises.filter((p) => p.settingName !== name && p.organisationId === organisationId);
            cachedSetting = this.cacheSetting({
                settingName: name,
                value,
                organisationId,
                cacheCreatedTimestamp: new Date().getTime()
            });
        });
        await settingValue;
        return cachedSetting.value;
    }

    public async getVenueSettingValue(name: string, venueId: string): Promise<string> {
        const existingPromise = this.findExistingPromise(name, null, venueId, 'venueId');
        if (existingPromise) {
            const promise = await existingPromise.p;
            return promise;
        }
        const settingsCache = localStorage.getItem('defaultSettings');
        const settingsCacheParsed = settingsCache ? JSON.parse(settingsCache) : [];
        const settingFromCache = this.findSettingFromCache(settingsCacheParsed, name, null, venueId, 'venueId');
        if (settingFromCache) {
            return settingFromCache.value;
        }
        const settingValue = this.getVenueSettingsByName(name, venueId);
        this.venueSettingPromises.push({ settingName: name, venueId, p: settingValue });
        let cachedSetting;
        settingValue.then((value) => {
            this.venueSettingPromises = this.venueSettingPromises.filter((p) => p.settingName !== name && p.venueId === venueId);
            cachedSetting = this.cacheSetting({
                settingName: name,
                value,
                venueId,
                cacheCreatedTimestamp: new Date().getTime()
            });
        });
        await settingValue;
        return cachedSetting.value;
    }

    public async getOrganisationSettingValueByName(name: string, organisationId: string): Promise<string> {
        const defaultSetting: DefaultSettingView[] = await this.sdkDefaultSettingsService.defaultSettingsGet
            (AppConfiguration.rsh.getStrVal('rid'),
            'OrganisationSettings',
            null, null, null, null, null, 1, 0, null, null, name, null, null, null, null,
            null, null, null, organisationId, null, 'body').toPromise();
        return defaultSetting[0].actualValue;
    }

    public async getVenueSettingsByName(name: string, venueId: string): Promise<string> {
        const defaultSetting: DefaultSettingView[] = await this.sdkDefaultSettingsService.defaultSettingsGet
            (AppConfiguration.rsh.getStrVal('rid'),
            'VenueSettings',
            null, null, null, null, null, 1, 0, null, null, name, null, null, null, null,
            null, null, null, null, venueId, 'body').toPromise();
        return defaultSetting[0].actualValue;
    }

    public findExistingPromise(settingName: string, organisationId: string, venueId: string, field: 'organisationId' | 'venueId'): IOrgSettingPromise {
        const id = field === 'organisationId' ? organisationId : venueId;
        const promises = field === 'organisationId' ? this.orgSettingPromises : this.venueSettingPromises;
        return promises.find((p) => p.settingName === settingName && p[field] === id);
    }

    public findSettingFromCache(cache: ISettingCache[], settingName: string, organisationId: string, venueId: string, field: 'organisationId' | 'venueId'): ISettingCache {
        const id = field === 'organisationId' ? organisationId : venueId;
        const setting = cache.find((c) => c.settingName === settingName && c[field] === id);
        if (!setting) { return null; }

        return this.checkSettingExpiry(cache, setting);
    }

    public checkSettingExpiry(cache: ISettingCache[], setting: ISettingCache): ISettingCache {
        const currentTime = new Date().getTime();

        if (currentTime - setting.cacheCreatedTimestamp < this.cacheExpirationTime) { return setting; }

        // If expired, remove the cache and return null
        this.removeSettingFromCache(cache, setting);
        return null;
    }

    public cacheSetting(setting: ISettingCache): ISettingCache {
        const defaultSettingsCache = JSON.parse(localStorage.getItem('defaultSettings')) || [];
        defaultSettingsCache.push({ ...setting });
        localStorage.setItem('defaultSettings', JSON.stringify(defaultSettingsCache));
        return setting;
    }

    public removeSettingFromCache(cache: ISettingCache[], setting: ISettingCache): void {
        const updatedCache = cache.filter((cachedSetting) => cachedSetting !== setting);
        localStorage.setItem('defaultSettings', JSON.stringify(updatedCache));
    }

    public cacheAllSettings(settings: DefaultSettingView[]): void {
        const cacheData = settings.map((s) => ({
            settingName: s.name,
            value: s.actualValue,
            organisationId: s.organisationId,
            venueId: s.venueId,
            cacheCreatedTimestamp: new Date().getTime()
        }));
        localStorage.setItem('defaultSettings', JSON.stringify(cacheData));
    }

}
